import { ROUTES } from '../../router/routes'

// Check if this is an AppSumo flow
export const isAppSumoFlow = (location) => {
    return location?.state?.fromAppSumoOAuth || false
}

// Get AppSumo display data from location state
export const getAppSumoData = (location) => {
    if (!location?.state?.fromAppSumoOAuth) return null
    return {
        license: location.state.appsumoLicense,
        tier: location.state.appsumoTier,
        displayName: location.state.appsumoDisplayName,
    }
}

// Store AppSumo data in session
export const storeAppSumoSession = (licenseData, authCode) => {
    sessionStorage.setItem('pendingAppSumoLicense', licenseData.licenseKey)
    sessionStorage.setItem('pendingAppSumoTier', licenseData.tier)
    sessionStorage.setItem('pendingAppSumoDisplayName', licenseData.displayName)
    sessionStorage.setItem('pendingAppSumoAuthCode', authCode)
}

// Get stored AppSumo data
export const getStoredAppSumoData = () => {
    const license = sessionStorage.getItem('pendingAppSumoLicense')
    if (!license) return null

    return {
        license,
        tier: sessionStorage.getItem('pendingAppSumoTier'),
        displayName: sessionStorage.getItem('pendingAppSumoDisplayName'),
        authCode: sessionStorage.getItem('pendingAppSumoAuthCode'),
    }
}

// Clear AppSumo session data
export const clearAppSumoSession = () => {
    sessionStorage.removeItem('pendingAppSumoLicense')
    sessionStorage.removeItem('pendingAppSumoTier')
    sessionStorage.removeItem('pendingAppSumoDisplayName')
    sessionStorage.removeItem('pendingAppSumoAuthCode')
}

// Get provisioning data for signup
export const getAppSumoProvisioningData = (location) => {
    const stored = getStoredAppSumoData()
    const isAppSumo = isAppSumoFlow(location) || stored

    if (!isAppSumo || !stored) return {}

    return {
        accountType: stored.tier,
    }
}

// Handle OAuth exchange only - no linking
export const exchangeAppSumoCode = async (code, server) => {
    return new Promise((resolve) => {
        // Exchange code for license (uses server without auth)
        // Using the isSlack parameter as skipAuth - when true, auth header is only added if token exists
        server.sheetPostData(
            {
                action: 'exchangeAppSumoCode',
                code: code,
            },
            (response) => {
                // Handler callback - response received successfully
                // The response has the license data nested under 'license' property
                const licenseData = response.license

                if (!licenseData || !licenseData.success || !licenseData.licenseKey) {
                    resolve({
                        success: false,
                        error: response.error || 'Invalid AppSumo authorization',
                    })
                    return
                }

                // Check if already claimed by another user
                if (licenseData.claimedBy && !licenseData.isClaimedByCurrentUser) {
                    resolve({
                        success: false,
                        error: 'This AppSumo license has already been claimed',
                    })
                    return
                }

                // Always store the license data in session storage
                storeAppSumoSession(licenseData, code)

                resolve({
                    success: true,
                    licenseData: licenseData,
                    isAlreadyClaimed: licenseData.isClaimedByCurrentUser,
                })
            },
            null,
            (error) => {
                console.error('AppSumo OAuth error:', error)
                resolve({ success: false, error: 'An error occurred during exchange' })
            },
            true // Using isSlack parameter to skip auth requirement
        )
    })
}

// Link AppSumo license to authenticated user
export const linkAppSumoLicense = async (licenseKey, server) => {
    return new Promise((resolve) => {
        server.sheetPostData(
            {
                action: 'linkAppSumoLicense',
                licenseKey: licenseKey,
            },
            (linkResponse) => {
                // The response has the license data nested under 'license' property
                const linkResult = linkResponse.license

                if (linkResult && linkResult.success) {
                    // Clear session storage after successful linking
                    clearAppSumoSession()
                    resolve({
                        success: true,
                        message: linkResult.message || 'AppSumo license activated!',
                    })
                } else {
                    resolve({
                        success: false,
                        error: linkResponse.errorMessage || 'Failed to activate license',
                    })
                }
            },
            null,
            (error) => {
                console.error('AppSumo link error:', error)
                resolve({ success: false, error: 'Failed to link license' })
            }
        )
    })
}

// Handle post-login license linking (works with both postData function and Server instance)
export const linkPendingAppSumoLicense = async (postDataOrServer) => {
    const stored = getStoredAppSumoData()
    if (!stored || !stored.license) return null

    // Check if it's a function (postData) or Server instance
    if (typeof postDataOrServer === 'function') {
        // It's postData function (from Login component)
        try {
            const response = await postDataOrServer({
                action: 'linkAppSumoLicense',
                licenseKey: stored.license,
            })

            // The response has the license data nested under 'license' property
            const licenseResult = response.license

            if (licenseResult && licenseResult.success) {
                clearAppSumoSession()
                return {
                    success: true,
                    message:
                        licenseResult.message || `AppSumo ${stored.displayName} plan activated!`,
                }
            }

            return { success: false, error: response.errorMessage || 'Failed to activate license' }
        } catch (error) {
            console.error('Error linking AppSumo license:', error)
            return { success: false, error: error.message }
        }
    } else {
        // It's a Server instance (from signup flow)
        return new Promise((resolve) => {
            postDataOrServer.sheetPostData(
                {
                    action: 'linkAppSumoLicense',
                    licenseKey: stored.license,
                },
                (response) => {
                    // The response has the license data nested under 'license' property
                    const licenseResult = response.license

                    if (licenseResult && licenseResult.success) {
                        clearAppSumoSession()
                        resolve({
                            success: true,
                            message:
                                licenseResult.message ||
                                `AppSumo ${stored.displayName} plan activated!`,
                        })
                    } else {
                        resolve({
                            success: false,
                            error: response.errorMessage || 'Failed to activate license',
                        })
                    }
                },
                null,
                (error) => {
                    console.error('Error linking AppSumo license:', error)
                    resolve({ success: false, error: error.message })
                }
            )
        })
    }
}
